import { useState } from 'react';
import type { TargetDTO } from '@devrobot/shared';
import { Eraser, Info, RefreshCw, WifiOff } from 'lucide-react';
import { ApiError } from '../../api/client';
import { useTargetLogs } from '../../api/queries';
import { ErrorAlert } from '../../components/ErrorAlert';
import { LogViewer } from '../../components/LogViewer';

const TAILS = [100, 200, 500, 1000, 2000];

export function LogsTab({ target: t }: { target: TargetDTO }) {
  const [service, setService] = useState('');
  const [tail, setTail] = useState(200);
  const [follow, setFollow] = useState(false);
  // Timestamp (ms) of the newest line when the view was cleared; lines up to it stay hidden.
  const [clearedAfter, setClearedAfter] = useState<number | null>(null);
  const enabled = t.can.viewLogs && !t.removedAt;
  const q = useTargetLogs(t.id, { service, tail, follow, enabled });
  const services = [...new Set([...t.state.services.map((s) => s.service), ...t.routes.map((r) => r.service)])].sort();

  if (!t.can.viewLogs) {
    return (
      <div role="note" className="alert alert-info alert-soft text-sm">
        <Info className="size-4 shrink-0" aria-hidden="true" />
        <span>
          Viewing runtime logs of {t.environment} needs the GitLab {t.environmentProtected ? 'Maintainer' : 'Developer'} role on this project.
        </span>
      </div>
    );
  }
  if (t.removedAt) return <p className="text-sm text-base-content/60">The application was removed; there are no running containers to read logs from.</p>;

  const agentDown = q.error instanceof ApiError && (q.error.code === 'agent_unavailable' || q.error.status === 503) ? q.error : null;
  const all = q.data?.lines ?? [];
  const visible = clearedAfter === null ? all : all.filter((l) => Date.parse(l.ts) > clearedAfter);

  return (
    <div className="space-y-3">
      {agentDown ? (
        <div role="alert" className="alert alert-warning alert-soft items-start text-sm">
          <WifiOff className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          <div className="space-y-0.5">
            <p className="font-semibold">Live logs are unavailable</p>
            <p>{agentDown.message}</p>
            <p className="text-base-content/70">The application may still be running normally; DevRobot just cannot reach the agent on {t.vps.name} right now. Logs return when the agent reconnects.</p>
          </div>
        </div>
      ) : (
        <ErrorAlert error={q.error} title="Logs could not be loaded" />
      )}
      <LogViewer
        busy={q.isFetching}
        lines={visible.map((l, i) => ({ key: i, ts: l.ts, source: services.length > 1 || !service ? l.service : undefined, message: l.message }))}
        emptyText={q.isPending ? 'Loading…' : clearedAfter !== null ? 'Cleared. New output appears here as it arrives.' : 'No log output for this selection.'}
        toolbar={
          <>
            <select className="select select-sm w-auto max-w-48" value={service} onChange={(e) => (setService(e.target.value), setClearedAfter(null))} aria-label="Service">
              <option value="">All services</option>
              {services.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <select className="select select-sm w-auto" value={tail} onChange={(e) => setTail(Number(e.target.value))} aria-label="Number of lines">
              {TAILS.map((n) => (
                <option key={n} value={n}>
                  last {n}
                </option>
              ))}
            </select>
            <label className="flex cursor-pointer items-center gap-2 text-base-content/80">
              <input type="checkbox" className="toggle toggle-sm toggle-primary" checked={follow} onChange={(e) => setFollow(e.target.checked)} />
              Follow (every 3 s)
            </label>
            <button type="button" className="btn btn-ghost btn-sm btn-square" onClick={() => void q.refetch()} aria-label="Refresh logs" disabled={q.isFetching}>
              <RefreshCw className={`size-4 ${q.isFetching ? 'animate-spin' : ''}`} aria-hidden="true" />
            </button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setClearedAfter(all.reduce((m, l) => Math.max(m, Date.parse(l.ts) || 0), 0))} disabled={visible.length === 0}>
              <Eraser className="size-4" aria-hidden="true" /> Clear
            </button>
            {clearedAfter !== null ? (
              <button type="button" className="btn btn-link btn-xs" onClick={() => setClearedAfter(null)}>
                Show all
              </button>
            ) : null}
          </>
        }
      />
      <p className="text-xs text-base-content/55">Known secret values of this target are masked in the output. Masking reduces, but cannot guarantee against, disclosure by application output.</p>
    </div>
  );
}
